import { useEffect, useId, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  Control,
  Controller,
  useFieldArray,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useLazySearchSystemUsersQuery } from '@/app/services/lookups';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import {
  type AnimalRow,
  firstFailureMessage,
  type ProcedureCatalogOption,
  procedureCatalogQueryOptions,
  useCreateAnimalProcedures,
} from '@/services/animal-management';
import { useCurrentUser } from '@/services/auth/util';
import { useToast } from '@/services/toast';

import { buildProcedureRequests } from './write-builders';

type ComboboxOption = { id: string; name: string };

const ENTERED_BY_SEARCH_LIMIT = 10;

type SearchableComboboxProps<T extends ComboboxOption> = {
  id: string;
  items: T[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  invalid?: boolean;
  onInputChange?: (value: string) => void;
  onValueChange?: (option: T | null) => void;
};

function SearchableCombobox<T extends ComboboxOption>({
  id,
  items,
  value,
  onChange,
  placeholder,
  invalid,
  onInputChange,
  onValueChange,
}: SearchableComboboxProps<T>) {
  const { t } = useTranslation();
  const selectedOption = items.find((option) => option.id === value);

  return (
    <Combobox<T>
      items={items}
      value={selectedOption}
      onValueChange={(option) => {
        onChange(option?.id ?? '');
        onValueChange?.(option);
      }}
      itemToStringValue={(option) => option.id}
      itemToStringLabel={(option) => option.name}
    >
      <ComboboxInput
        id={id}
        aria-invalid={invalid}
        className="h-full w-full"
        placeholder={placeholder}
        showClear
        onChange={
          onInputChange
            ? (e: React.ChangeEvent<HTMLInputElement>) =>
                onInputChange(e.target.value)
            : undefined
        }
      />
      <ComboboxContent>
        <ComboboxEmpty>
          {t('common.noItemsFound', 'No items found.')}
        </ComboboxEmpty>
        <ComboboxList>
          {(option: T) => (
            <ComboboxItem key={option.id} value={option}>
              {option.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

function useSchema() {
  const { t } = useTranslation();
  return useMemo(() => {
    const required = t('common.required', 'Required');
    const addProcedureItemSchema = z.object({
      procedureCatalogId: z.string().trim().min(1, required),
      description: z.string().trim().min(1, required),
      enteredById: z.string().trim().min(1, required),
      date: z
        .date()
        .optional()
        .refine((value) => value instanceof Date, required),
    });
    return z.object({
      procedures: z.array(addProcedureItemSchema).min(1),
    });
  }, [t]);
}

type AddProceduresFormValues = z.infer<ReturnType<typeof useSchema>>;
type AddProcedureFormItem = AddProceduresFormValues['procedures'][number];

type AddProceduresDialogProps = {
  open: boolean;
  selectedAnimals: AnimalRow[];
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

const createEmptyProcedure = (enteredById = ''): AddProcedureFormItem => ({
  procedureCatalogId: '',
  description: '',
  enteredById,
  date: undefined as unknown as Date,
});

type ProcedureCardProps = {
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  control: Control<AddProceduresFormValues>;
  form: UseFormReturn<AddProceduresFormValues>;
  procedureCatalogOptions: ProcedureCatalogOption[];
  currentUserOption?: ComboboxOption;
};

function ProcedureCard({
  index,
  canRemove,
  onRemove,
  control,
  form,
  procedureCatalogOptions,
  currentUserOption,
}: ProcedureCardProps) {
  const { t } = useTranslation();

  // Per-card Entered By search state and lazy query
  const [_enteredBySearch, setEnteredBySearch] = useState('');
  const [triggerSearch, { data: searchedUsers }] =
    useLazySearchSystemUsersQuery();

  const handleEnteredBySearch = (search: string) => {
    setEnteredBySearch(search);
    if (search.length >= 1) {
      triggerSearch({
        search,
        selectionLimit: ENTERED_BY_SEARCH_LIMIT,
      });
    }
  };

  const enteredByOptions = useMemo(() => {
    const mappedUsers: ComboboxOption[] = (searchedUsers ?? []).map((user) => ({
      id: user.id,
      name: user.fullName ?? user.internalEmailAddress ?? 'Unknown User',
    }));

    const combined = [
      ...(currentUserOption ? [currentUserOption] : []),
      ...mappedUsers,
    ];

    const seen = new Set<string>();
    return combined.filter((option) => {
      if (seen.has(option.id)) return false;
      seen.add(option.id);
      return true;
    });
  }, [currentUserOption, searchedUsers]);

  return (
    <Card size="sm" className="gap-2">
      <CardHeader>
        <CardTitle>
          {t('animalManagement.addProcedures.procedureNumber', {
            defaultValue: 'Procedure {index}',
            index: index + 1,
          })}
        </CardTitle>
        {canRemove && (
          <CardAction>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onRemove}
              aria-label={t('animalManagement.addProcedures.removeProcedure', {
                defaultValue: 'Remove procedure {index}',
                index: index + 1,
              })}
            >
              <XIcon className="size-4" />
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <Controller
          name={`procedures.${index}.procedureCatalogId`}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid} className="md:col-span-2">
              <FieldLabel htmlFor={ctrl.name}>
                {t(
                  'animalManagement.addProcedures.procedureCodeName',
                  'Procedure Code - Name',
                )}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <SearchableCombobox
                id={ctrl.name}
                items={procedureCatalogOptions}
                value={ctrl.value}
                onChange={ctrl.onChange}
                placeholder={t(
                  'animalManagement.addProcedures.procedurePlaceholder',
                  'Select from catalog...',
                )}
                invalid={fieldState.invalid}
                onValueChange={(option) => {
                  if (option) {
                    form.setValue(
                      `procedures.${index}.description`,
                      option.description,
                      { shouldDirty: true, shouldValidate: true },
                    );
                  }
                }}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name={`procedures.${index}.description`}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid} className="md:col-span-2">
              <FieldLabel htmlFor={ctrl.name}>
                {t('animalManagement.columns.description', 'Description')}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <Textarea
                {...ctrl}
                id={ctrl.name}
                aria-invalid={fieldState.invalid}
                placeholder={t(
                  'animalManagement.addProcedures.descriptionPlaceholder',
                  'Procedure description...',
                )}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name={`procedures.${index}.enteredById`}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={ctrl.name}>
                {t('animalManagement.addProcedures.enteredBy', 'Entered By')}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <SearchableCombobox
                id={ctrl.name}
                items={enteredByOptions}
                value={ctrl.value}
                onChange={ctrl.onChange}
                placeholder={t(
                  'animalManagement.addProcedures.enteredByPlaceholder',
                  'Search user...',
                )}
                invalid={fieldState.invalid}
                onInputChange={handleEnteredBySearch}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name={`procedures.${index}.date`}
          control={control}
          render={({ field: ctrl, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={ctrl.name}>
                {t('common.date', 'Date')}
                <span className="text-destructive">*</span>
              </FieldLabel>
              <DatePicker
                id={ctrl.name}
                value={ctrl.value}
                onChange={ctrl.onChange}
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </CardContent>
    </Card>
  );
}

export function AddProceduresDialog({
  open,
  selectedAnimals,
  onOpenChange,
  onSuccess,
}: AddProceduresDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const schema = useSchema();
  const { data: currentUser } = useCurrentUser();
  const { data: procedureCatalogOptions = [] } = useQuery(
    procedureCatalogQueryOptions(),
  );
  const toast = useToast();
  const createProcedures = useCreateAnimalProcedures();

  const currentUserOption = useMemo<ComboboxOption | undefined>(() => {
    if (!currentUser?.userId) return undefined;
    return {
      id: currentUser.userId,
      name: currentUser.displayName ?? currentUser.email,
    };
  }, [currentUser?.userId, currentUser?.displayName, currentUser?.email]);

  const form = useForm<AddProceduresFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      procedures: [createEmptyProcedure(currentUserOption?.id)],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'procedures',
  });

  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [form, open, selectedAnimals]);

  // Backfill empty enteredById fields when currentUser resolves async
  useEffect(() => {
    if (!open || !currentUserOption?.id) return;

    const procedures = form.getValues('procedures');
    let hasChanges = false;

    procedures.forEach((procedure, index) => {
      if (!procedure.enteredById) {
        form.setValue(`procedures.${index}.enteredById`, currentUserOption.id, {
          shouldValidate: true,
        });
        hasChanges = true;
      }
    });

    if (hasChanges) {
      form.trigger('procedures');
    }
  }, [open, currentUserOption?.id, form]);

  const totalRecords = selectedAnimals.length * fields.length;

  const onSubmit = async (values: AddProceduresFormValues) => {
    const requests = buildProcedureRequests(
      selectedAnimals,
      values.procedures,
      currentUserOption?.id,
    );
    const results = await createProcedures.mutateAsync(requests);
    const succeeded = results.filter((r) => r.success).length;

    if (succeeded === results.length) {
      toast.show({
        severity: 'success',
        summary: t('common.success', 'Success'),
        detail: t('animalManagement.writes.proceduresCreated', {
          defaultValue: '{count} procedure record(s) created.',
          count: succeeded,
        }),
      });
      onOpenChange(false);
    } else {
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t('animalManagement.writes.partialProcedures', {
          defaultValue:
            '{succeeded} of {total} procedure records created. {message}',
          succeeded,
          total: results.length,
          message: firstFailureMessage(results) ?? '',
        }),
        life: 10000,
      });
    }
    if (succeeded > 0) onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('animalManagement.addProcedures.title', 'Add Procedures')}
          </DialogTitle>
          <DialogDescription>
            {t('animalManagement.addProcedures.description', {
              defaultValue: 'Add procedures to {count} selected animals',
              count: selectedAnimals.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody
          as="form"
          id={formId}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <Card className="border-blue-200 bg-blue-50/70">
            <CardHeader>
              <CardTitle>
                {t('animalManagement.addProcedures.selectedAnimals', {
                  defaultValue: 'Selected Animals ({count})',
                  count: selectedAnimals.length,
                })}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {selectedAnimals.map((animal) => (
                <div
                  key={animal.id}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1.5 text-sm text-blue-900"
                >
                  <span className="font-medium">{animal.nameTag}</span>
                  <span className="rounded bg-blue-200 px-2 py-0.5 text-xs font-medium text-blue-700">
                    {animal.animalType}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardHeader>
              <CardTitle>
                {t(
                  'animalManagement.addProcedures.proceduresToAssign',
                  'Procedures to Assign',
                )}
              </CardTitle>
              <CardDescription>
                {t(
                  'animalManagement.addProcedures.proceduresHelper',
                  'Add one or more procedures that will be created for each selected animal.',
                )}
              </CardDescription>
              <CardAction>
                <Button
                  type="button"
                  variant="outline"
                  className="bg-white"
                  onClick={() =>
                    append(createEmptyProcedure(currentUserOption?.id ?? ''))
                  }
                >
                  <PlusIcon className="mr-2 size-4" />
                  {t(
                    'animalManagement.addProcedures.addProcedure',
                    'Add Procedure',
                  )}
                </Button>
              </CardAction>
            </CardHeader>
          </Card>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <ProcedureCard
                key={field.id}
                index={index}
                canRemove={fields.length > 1}
                onRemove={() => remove(index)}
                control={form.control}
                form={form}
                procedureCatalogOptions={procedureCatalogOptions}
                currentUserOption={currentUserOption}
              />
            ))}
          </div>

          <div className="rounded-lg border border-emerald-300 bg-emerald-100 px-3 py-2 text-sm text-emerald-900">
            <span className="font-semibold">
              {t('animalManagement.addProcedures.summaryLabel', 'Summary:')}
            </span>{' '}
            {t('animalManagement.addProcedures.summaryText', {
              defaultValue:
                '{procedures} procedure(s) × {animals} animal(s) = {total} total records will be created',
              animals: selectedAnimals.length,
              procedures: fields.length,
              total: totalRecords,
            })}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <XIcon className="mr-2 size-4" />
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={
              !form.formState.isValid ||
              selectedAnimals.length === 0 ||
              createProcedures.isPending
            }
          >
            <CheckIcon className="mr-2 size-4" />
            {t('animalManagement.addProcedures.submit', {
              defaultValue: 'Add {count} Procedure Records',
              count: totalRecords,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
